import DeleteModal from "components/shared/DeleteModal";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InvoicePercentage } from "store/invoicePercentages";
import { deleteInvoicePercentage, fetchInvoicePercentages } from "store/invoicePercentages";
import { useAppDispatch, useAppSelector } from "store/store";

import InvoicePercentageBlock from "./InvoicePercentageBlock";
import InvoicePercentageForm from "./InvoicePercentageForm";
import styles from "./style.module.scss";

// Main page for setting mapping percentages in invoices.
const InvoicePercentages = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [modalType, setModalType] = useState("");
    const [formMode, setFormMode] = useState<"new" | "edit">();
    const [currentPercentage, setCurrentPercentage] = useState<InvoicePercentage>();

    const { invoicePercentages, invoicePercentagesStatus } = useAppSelector((state) => state.invoicePercentages);

    /**
     * Opens edit modal.
     * @param percentage Data of the match to update.
     */
    const handleEditModal = async (percentage: InvoicePercentage) => {
        await setCurrentPercentage(percentage);
        await setFormMode("edit");
        setModalType("edit");
    };

    /**
     * Closes modal no matter the type.
     */
    const handleCloseModal = async () => {
        await setCurrentPercentage(undefined);
        setModalType("");
    };

    /**
     * Opens delete modal.
     * @param percentage Data of the match to delete.
     */
    const handleDeleteModal = async (percentage: InvoicePercentage) => {
        await setCurrentPercentage(percentage);
        setModalType("delete");
    };

    /**
     * Deletes a percentage line.
     */
    const handleDeletePercentage = async () => {
        if (currentPercentage) {
            const response = await dispatch(deleteInvoicePercentage({ percentageId: currentPercentage?.id }));
            if (response.meta.requestStatus === "fulfilled") {
                setCurrentPercentage(undefined);
                setModalType("");
            }
        }
    };

    // Keyed on the fetch status, not the list: a company without commissions gets a new [] on every
    // fetch, which re-triggered this effect and polled the API in a loop
    useEffect(() => {
        if (invoicePercentagesStatus === "idle") {
            dispatch(fetchInvoicePercentages());
        }
    }, [invoicePercentagesStatus]);

    return (
        <div className={styles["invoice-percentages-container"]}>
            <div className={styles["page-header"]}>
                <div className={styles["header-title"]}>
                    <h1>{t("Settings.InvoicePercentages")}</h1>
                    <StyledButton
                        buttonType="primary"
                        onClick={() => {
                            setModalType("create"), setFormMode("new");
                        }}
                    >
                        {t("InvoicePercentages.NewPercentage")}
                    </StyledButton>
                </div>
                <span>{t("InvoicePercentages.PageSubtitle")}</span>
            </div>
            <div className={styles["page-content"]}>
                {invoicePercentages.map((percentage) => (
                    <InvoicePercentageBlock
                        key={`invoicePercentage-${percentage.id}`}
                        percentage={percentage}
                        handleEdit={handleEditModal}
                        handleDelete={handleDeleteModal}
                    />
                ))}
                <ModalBase
                    open={["create", "edit"].includes(modalType)}
                    onClose={() => setModalType("")}
                    modalTitle={t("InvoicePercentages.Form.Title")}
                    modalSubtitle={t("InvoicePercentages.Form.Subtitle")}
                >
                    <InvoicePercentageForm
                        formMode={formMode}
                        invoicePercentage={currentPercentage}
                        closeModal={handleCloseModal}
                    />
                </ModalBase>
                <DeleteModal
                    open={modalType === "delete"}
                    onClose={handleCloseModal}
                    onDelete={handleDeletePercentage}
                    elementTitle={t("InvoicePercentages.Block.ThisMatch", { name: currentPercentage?.name })}
                />
            </div>
        </div>
    );
};

export default InvoicePercentages;
